import { RoomDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { roomDTOtoResource } from 'kits/apiKit3/legacy';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useRecoilCallback } from 'recoil';
import { resourceService } from 'services/api/resource.service';
import { aLastRoomListFetchKey, aRoomList, aRoomListLoading } from 'shared-state/directory/atoms';
import {
    useAreaDirectoryLoading,
    useAreaGroupList,
    useAreaList,
    useCampus,
    useRefetchAreaAndAreaGroupList,
    useSelectedVisitor
} from 'shared-state/directory/hooks';
import { useIdentityClaims } from 'shared-state/identity/hooks';
import { useCurrentAreaId, useCurrentCampusId, useCurrentOrgId, useCurrentRoomId } from 'shared-state/location/hook';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ResourceAdapter: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const params = useParams<{ areaId?: string; roomId?: string }>();

    const [identityClaims] = useIdentityClaims();
    const [currentOrgId] = useCurrentOrgId();
    const [currentCampusId, setCurrentCampusId] = useCurrentCampusId();
    const [currentAreaId, setCurrentAreaId] = useCurrentAreaId();
    const [, setCurrentRoomId] = useCurrentRoomId();

    const routeHistory = useNavigate();
    const [, setSelectedVisitor] = useSelectedVisitor();

    const [, setCampus] = useCampus();
    const [, setAreaList] = useAreaList();
    const [, setAreaGroupList] = useAreaGroupList();
    const [, setAreaDirectoryLoading] = useAreaDirectoryLoading();
    const [refetchAreaAndAreaGroupList, setRefetchAreaAndAreaGroupList] = useRefetchAreaAndAreaGroupList();
    const roomListRequestIdRef = useRef(0);
    /** Bumps when a new refetch-directory batch starts; avoids leaving aAreaDirectoryLoading stuck when cleanup runs after the 1s refetch-flag timeout while fetches are still in flight. */
    const refetchDirectoryBatchIdRef = useRef(0);

    /** URL wins over Recoil so area switches don't paint one frame with stale area id + old rooms. */
    const effectiveAreaId = useMemo(() => params.areaId ?? currentAreaId, [params.areaId, currentAreaId]);

    const clearStaleRoomListIfContextChanged = useRecoilCallback(
        ({ snapshot, set }) =>
            () => {
                if (!currentCampusId || !effectiveAreaId || effectiveAreaId === 'waiting-area') {
                    return;
                }
                const key = `${currentCampusId}|${effectiveAreaId}`;
                const lastKey = snapshot.getLoadable(aLastRoomListFetchKey).getValue();
                if (lastKey !== key) {
                    set(aRoomList, []);
                    set(aRoomListLoading, true);
                }
            },
        [currentCampusId, effectiveAreaId]
    );

    useLayoutEffect(() => {
        clearStaleRoomListIfContextChanged();
    }, [clearStaleRoomListIfContextChanged]);

    const fetchRoomsForContext = useRecoilCallback(
        ({ snapshot, set }) =>
            async (campusId: string | undefined, areaId: string | undefined) => {
                const requestId = ++roomListRequestIdRef.current;
                if (!campusId || !areaId || areaId === 'waiting-area') {
                    if (requestId === roomListRequestIdRef.current) {
                        set(aRoomListLoading, false);
                        set(aRoomList, []);
                        set(aLastRoomListFetchKey, null);
                    }
                    return console.warn('Not able to fetch Rooms');
                }

                const key = `${campusId}|${areaId}`;
                const roomList = snapshot.getLoadable(aRoomList).getValue();
                const lastKey = snapshot.getLoadable(aLastRoomListFetchKey).getValue();
                const skipLoadingIndicator = lastKey === key && roomList.length > 0;

                // Drop stale rooms immediately when campus/area context changes (avoid one frame of wrong list).
                if (lastKey !== key) {
                    set(aRoomList, []);
                }

                if (!skipLoadingIndicator && requestId === roomListRequestIdRef.current) {
                    set(aRoomListLoading, true);
                }
                try {
                    const roomDTOs: any = await resourceService.getAllForArea(campusId, areaId);

                    if (!roomDTOs) return;

                    const roomResources = roomDTOs.map((room: RoomDto) => roomDTOtoResource(room));
                    if (requestId === roomListRequestIdRef.current) {
                        set(aRoomList, roomResources);
                        set(aLastRoomListFetchKey, key);
                    }
                } finally {
                    if (requestId === roomListRequestIdRef.current) {
                        set(aRoomListLoading, false);
                    }
                }
            },
        []
    );

    const fetchCampus = async () => {
        if (!currentOrgId || currentOrgId != identityClaims?.tid) return console.warn('Not able to fetch campus', currentOrgId, identityClaims?.tid);
        const campusDTO = await resourceService.getCampusById(currentOrgId);

        if (!campusDTO) return; // Error @TODO handle

        setCampus(campusDTO);
        setCurrentCampusId(campusDTO.id);
    };

    const fetchAreas = async () => {
        if (!currentCampusId) {
            setAreaList([]);
            return console.warn('Not able to fetch areas');
        }
        const areaDTOs = await resourceService.getAllForCampus(currentCampusId);

        if (!areaDTOs) return; // Error @TODO handle

        setAreaList(areaDTOs);
    };

    const fetchAreaGroups = async () => {
        if (!currentCampusId) {
            setAreaGroupList([]);
            return console.warn('Not able to fetch area groups');
        }
        const areaGroupDTOs = await resourceService.getAllAreaGroupsForCampus(currentCampusId);

        if (!areaGroupDTOs) return; // Error @TODO handle

        setAreaGroupList(areaGroupDTOs);
    };

    const handleError = (err: any) => {
        let loginRequired = false;
        console.error('[RESOURCE ADAPTER]', err.message);

        if (err.message.includes('401')) loginRequired = true;
        if (err.message.includes('404')) loginRequired = true;

        if (loginRequired) routeHistory('/login');
    };

    useEffect(() => {
        fetchCampus().catch((err: any) => {
            handleError(err);

            // Retry later
            setTimeout(() => {
                fetchCampus().catch((err: any) => handleError(err));
            }, 1000);
        });
    }, [currentOrgId]);

    useEffect(() => {
        if (!currentCampusId) {
            return;
        }
        let cancelled = false;
        setAreaDirectoryLoading(true);
        Promise.all([fetchAreas().catch((err: any) => handleError(err)), fetchAreaGroups().catch((err: any) => handleError(err))]).finally(() => {
            if (!cancelled) {
                setAreaDirectoryLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [currentCampusId]);

    useEffect(() => {
        if (!refetchAreaAndAreaGroupList || !currentCampusId) {
            return;
        }
        const batchId = ++refetchDirectoryBatchIdRef.current;
        setAreaDirectoryLoading(true);
        Promise.all([
            fetchAreas().catch((err: any) => handleError(err)),
            fetchAreaGroups().catch((err: any) => handleError(err)),
            fetchRoomsForContext(currentCampusId, effectiveAreaId).catch((err: any) => handleError(err))
        ]).finally(() => {
            if (batchId === refetchDirectoryBatchIdRef.current) {
                setAreaDirectoryLoading(false);
            }
        });

        const clearRefetchFlagTimeoutId = window.setTimeout(() => {
            setRefetchAreaAndAreaGroupList(false);
        }, 1000);

        return () => {
            window.clearTimeout(clearRefetchFlagTimeoutId);
        };
    }, [refetchAreaAndAreaGroupList, currentCampusId, effectiveAreaId, fetchRoomsForContext]);

    useEffect(() => {
        fetchRoomsForContext(currentCampusId, effectiveAreaId).catch((err: any) => {
            handleError(err);
        });
    }, [currentCampusId, effectiveAreaId, fetchRoomsForContext]);

    useLayoutEffect(() => {
        params.areaId && localStorage.setItem('lastAreaId', params.areaId);
        setCurrentAreaId(params.areaId);
    }, [params.areaId]);

    useEffect(() => {
        const personalRoomName = 'personal-' + identityClaims?.uid + '_' + params.areaId;
        const nextRoomId = params.roomId ? params.roomId : personalRoomName || undefined;
        if (params.roomId?.includes('visitor-')) setSelectedVisitor(params.roomId.replace('visitor-', ''));
        else setSelectedVisitor(undefined);

        setCurrentRoomId(nextRoomId);
    }, [params.roomId, currentAreaId]);

    return <></>;
};
